import { useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, Tab, Tabs } from '@mui/material';
import useBoardStore from '../../store/boardStore';
import { OwnedBoardSummary, teamsSetupApi } from '../../services/teamsSetupApi';
import CreateBoardForm from './CreateBoardForm';
import ExistingBoardsList from './ExistingBoardsList';

export interface SelectedBoardResult {
  boardId: string;
  boardUrl: string;
  title: string;
  selectionType: 'created' | 'existing';
}

type BoardSelectionMode = 'create' | 'existing';

interface SetBoardDialogProps {
  appAuthToken: string | null;
  defaultTitle: string;
  loginError: string | null;
  isLoggedIn: boolean;
  isLoggingIn: boolean;
  onLogin: () => void;
  onBoardSelected: (result: SelectedBoardResult) => void;
}

const SetBoardDialog = ({
  appAuthToken,
  defaultTitle,
  loginError,
  isLoggedIn,
  isLoggingIn,
  onLogin,
  onBoardSelected,
}: SetBoardDialogProps) => {
  const clearActionError = useBoardStore((state) => state.clearActionError);
  const [createFormResetVersion, setCreateFormResetVersion] = useState(0);
  const [mode, setMode] = useState<BoardSelectionMode>('create');
  const [ownedBoards, setOwnedBoards] = useState<OwnedBoardSummary[]>([]);
  const [selectedExistingBoardId, setSelectedExistingBoardId] = useState('');
  const [isLoadingBoards, setIsLoadingBoards] = useState(false);
  const [boardsError, setBoardsError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    setMode('create');
    setCreateFormResetVersion((current) => current + 1);
    clearActionError();
  }, [clearActionError, defaultTitle, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !appAuthToken) {
      return;
    }

    let isCancelled = false;

    const run = async () => {
      setIsLoadingBoards(true);
      setBoardsError(null);

      try {
        const boards = await teamsSetupApi.getOwnedBoards(appAuthToken);
        if (isCancelled) return;

        setOwnedBoards(boards);
        setSelectedExistingBoardId((current) =>
          current.length > 0 && boards.some((board) => board.id === current)
            ? current
            : boards[0]?.id ?? ''
        );
      } catch (error) {
        if (isCancelled) return;
        const message = error instanceof Error ? error.message : 'Failed to load existing boards.';
        setBoardsError(message);
        setOwnedBoards([]);
        setSelectedExistingBoardId('');
      } finally {
        if (!isCancelled) {
          setIsLoadingBoards(false);
        }
      }
    };

    void run();

    return () => {
      isCancelled = true;
    };
  }, [appAuthToken, isLoggedIn]);

  const selectBoard = (
    boardId: string,
    selectedTitle: string,
    selectionType: 'created' | 'existing'
  ) => {
    if (!appAuthToken) {
      return;
    }

    const boardUrl = `${window.location.origin}/b/${boardId}?auth=${encodeURIComponent(
      appAuthToken
    )}`;
    onBoardSelected({
      boardId,
      boardUrl,
      title: selectedTitle.trim().length > 0 ? selectedTitle : 'Dotstorming Board',
      selectionType,
    });
  };

  const handleSelectExistingBoard = (boardId: string) => {
    const selectedBoard = ownedBoards.find((board) => board.id === boardId);
    if (!selectedBoard) {
      return;
    }

    setSelectedExistingBoardId(selectedBoard.id);
    selectBoard(selectedBoard.id, selectedBoard.title, 'existing');
  };

  return (
    <Box>
      <Stack spacing={2}>
        {!isLoggedIn ? (
          <>
            {loginError ? <Alert severity="error">{loginError}</Alert> : null}
            <Button
              variant="contained"
              onClick={onLogin}
              disabled={isLoggingIn}
              sx={{ alignSelf: 'flex-start' }}
            >
              {isLoggingIn ? 'Signing in…' : 'Login'}
            </Button>
          </>
        ) : (
          <>
            <Tabs value={mode} onChange={(_event, value: BoardSelectionMode) => setMode(value)}>
              <Tab value="create" label="Create new board" />
              <Tab value="existing" label="Use existing board" />
            </Tabs>
            <Box sx={{ p: 2, pt: 1 }}>
              {mode === 'create' ? (
                <CreateBoardForm
                  appAuthToken={appAuthToken}
                  resetVersion={createFormResetVersion}
                  onBoardCreated={(result) => selectBoard(result.boardId, result.title, 'created')}
                />
              ) : (
                <ExistingBoardsList
                  isLoadingBoards={isLoadingBoards}
                  boardsError={boardsError}
                  ownedBoards={ownedBoards}
                  selectedExistingBoardId={selectedExistingBoardId}
                  onSelectBoard={handleSelectExistingBoard}
                />
              )}
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default SetBoardDialog;
